import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { radioStations, type RadioStation } from '@/mocks/radios';
import { radioProfiles, getRadioTeam } from '@/mocks/radioProfile';
import { getStationLives } from '@/mocks/radioContent';
import { getRadioBySlug } from '@/services/radios';
import { mapRadioToStation } from './utils';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import RadioCoverHeader from './components/RadioCoverHeader';
import OfficialHeroHeader from './components/OfficialHeroHeader';
import MobileRadioProfile from './components/MobileRadioProfile';
import OfficialMobileProfile from './components/OfficialMobileProfile';
import RadioLiveSection from './components/RadioLiveSection';
import RadioFeedSection from './components/RadioFeedSection';
import RadioProgramasSection from './components/RadioProgramasSection';
import RadioLocutoresSection from './components/RadioLocutoresSection';
import RadioEventosSection from './components/RadioEventosSection';
import RadioConcursosSection from './components/RadioConcursosSection';
import RadioTopFansSection from './components/RadioTopFansSection';
import RadioPatrocinadoresSection from './components/RadioPatrocinadoresSection';
import RadioClubSection from './components/RadioClubSection';
import CabinaSidePanel from './components/CabinaSidePanel';
import { useCabinaOpen } from '@/context/CabinaContext';

// ══════════════════════════════════════════════════════════
// LOADING SKELETON — Mientras se consulta Supabase
// ══════════════════════════════════════════════════════════
function LoadingState() {
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-foreground-100 flex items-center justify-center animate-pulse">
            <i className="ri-loader-4-line text-3xl text-foreground-400 animate-spin"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">Buscando estación</h1>
          <p className="text-foreground-500">Verificando disponibilidad en la plataforma...</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PÁGINA DE RADIO
// ══════════════════════════════════════════════════════════
export default function RadioPage() {
  const { frequency } = useParams<{ frequency: string }>();

  const decodedId = frequency || '';

  // ── Búsqueda en mock ──
  const mockStation: RadioStation | undefined = radioStations.find((s) => {
    const freqHyphen = s.frequency.replace('.', '-');
    return s.slug === decodedId || s.frequency === decodedId || freqHyphen === decodedId;
  });

  // ── Fallback Supabase (cuando no está en el mock) ──
  const [loading, setLoading] = useState(!mockStation);
  const [supabaseStationData, setSupabaseStationData] = useState<RadioStation | null>(null);
  const [supabaseNotFound, setSupabaseNotFound] = useState(false);

  useEffect(() => {
    // Resetear estado al cambiar de slug
    setSupabaseStationData(null);
    setSupabaseNotFound(false);

    if (mockStation) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    getRadioBySlug(decodedId)
      .then((radio) => {
        if (cancelled) return;
        if (radio) {
          setSupabaseStationData(mapRadioToStation(radio));
        } else {
          setSupabaseNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSupabaseNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [decodedId, mockStation]);

  const station: RadioStation | undefined = mockStation || supabaseStationData || undefined;

  // ── Detección de radio de plataforma ──
  const isPlatformRadio = station?.isPlatformRadio === true;

  // ── Cabina ──
  const [cabinaOpen, setCabinaOpen] = useState(false);
  const { setCabinaOpen: setGlobalCabinaOpen } = useCabinaOpen();

  useEffect(() => {
    setGlobalCabinaOpen(cabinaOpen);
    return () => { setGlobalCabinaOpen(false); };
  }, [cabinaOpen, setGlobalCabinaOpen]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [frequency]);

  // ── Loading state ──
  if (loading) {
    return <LoadingState />;
  }

  // ── Not found ──
  if (!station) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-signal-wifi-error-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">Frecuencia no encontrada</h1>
            <p className="text-foreground-500 mb-6">La estacion que buscas no esta disponible en este momento.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl transition-all cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Volver al Dial
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const profile = radioProfiles[station.id];
  const team = getRadioTeam(station.id);
  const followers = 12500 + station.listeners * 2;
  const programsCount = profile?.schedule?.length || 4;
  const hostsCount = team
    ? (team.locutores.length + team.productores.length)
    : 3;
  const lives = getStationLives(station.id);

  return (
    <>
      {/* ═══ MOBILE ═══ */}
      {isPlatformRadio ? (
        <OfficialMobileProfile station={station} />
      ) : (
        <MobileRadioProfile station={station} />
      )}

      {/* ═══ DESKTOP ═══ */}
      <div className="hidden md:block min-h-screen bg-background-100/50 flex flex-col">
        {/* Header - hidden on this page since we have our own cover */}
        <div className="hidden">
          <Header />
        </div>

        {isPlatformRadio ? (
          <OfficialHeroHeader
            station={station}
            followers={followers}
            programsCount={programsCount}
            hostsCount={hostsCount}
            onOpenCabina={() => setCabinaOpen(true)}
          />
        ) : (
          <RadioCoverHeader
            station={station}
            followers={followers}
            programsCount={programsCount}
            hostsCount={hostsCount}
            onOpenCabina={() => setCabinaOpen(true)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 w-full">
          <div className="space-y-6">
            {/* Radio en Vivo */}
            <RadioLiveSection station={station} lives={lives} />

            {/* Feed Social */}
            <RadioFeedSection station={station} />

            {/* Programas */}
            <RadioProgramasSection station={station} />

            {/* Locutores */}
            <RadioLocutoresSection station={station} />

            {/* Eventos */}
            <RadioEventosSection station={station} />

            {/* Concursos */}
            <RadioConcursosSection station={station} />

            {/* Top Fans */}
            <RadioTopFansSection station={station} />

            {/* Patrocinadores */}
            <RadioPatrocinadoresSection station={station} />

            {/* Club Oficial RFW */}
            <RadioClubSection stationName={station.name} />
          </div>
        </main>

        {/* Footer */}
        <div className="hidden md:block">
          <Footer />
        </div>

        {/* Cabina Side Panel */}
        <CabinaSidePanel
          station={station}
          isOpen={cabinaOpen}
          onClose={() => setCabinaOpen(false)}
        />
      </div>
    </>
  );
}